/**
 * ProgressDots Component
 *
 * Displays progress indicator dots for multi-step flows
 */

import React from "react";
import { cn } from "../utils/cn";

export interface ProgressDotsProps {
  /** Total number of steps */
  total: number;
  /** Current step (0-indexed) */
  current: number;
  /** Optional class names to apply */
  className?: string;
}

/**
 * ProgressDots - Visual indicator of progress through multi-step flow
 *
 * Features:
 * - Dots represent each step
 * - Current step highlighted
 * - Past steps can be distinguished from future steps
 * - Accessible with proper ARIA labels
 *
 * @param props - Component props
 * @returns Progress dots indicator
 */
export const ProgressDots: React.FC<ProgressDotsProps> = ({
  total,
  current,
  className = "",
}) => {
  return (
    <div
      className={cn("progress-dots", className)}
      role="progressbar"
      aria-valuenow={current + 1}
      aria-valuemin={1}
      aria-valuemax={total}
      aria-label={`Step ${current + 1} of ${total}`}
    >
      {Array.from({ length: total }, (_, index) => (
        <div
          key={index}
          className={cn(
            "progress-dot",
            index === current && "progress-dot-active",
            index < current && "progress-dot-completed"
          )}
          aria-hidden="true"
        />
      ))}
    </div>
  );
};

export default ProgressDots;
